import { useState, useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';

export interface Project {
    id: string;
    name: string;
}

interface ProjectSelectorProps {
    projects: Project[];
    selectedProjectId: string;
    onSelectProject: (projectId: string) => void;
}

export function ProjectSelector({ projects, selectedProjectId, onSelectProject }: ProjectSelectorProps) {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const selectedProject = projects.find(p => p.id === selectedProjectId);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleProjectSelect = (projectId: string) => {
        onSelectProject(projectId);
        setIsOpen(false);
    };

    return (
        <div className="relative" ref={dropdownRef}>
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="w-full flex items-center justify-between px-3 py-2 bg-surface-hover text-foreground rounded-md hover:bg-surface-active transition-all duration-200"
            >
                <span className="truncate">{selectedProject?.name || 'Select Project'}</span>
                <ChevronDown
                    size={16}
                    className={`ml-2 text-muted-foreground transition-transform duration-200 ${isOpen ? 'rotate-180' : ''
                        }`}
                />
            </button>

            {isOpen && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-surface border border-border rounded-md shadow-lg z-10 max-h-60 overflow-y-auto">
                    {projects.map((project) => (
                        <button
                            key={project.id}
                            onClick={() => handleProjectSelect(project.id)}
                            className={`w-full text-left px-3 py-2 hover:bg-surface-hover transition-all duration-200 ${project.id === selectedProjectId
                                ? 'bg-surface-hover text-foreground'
                                : 'text-foreground-subtle'
                                }`}
                        >
                            {project.name}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
} 